import React from 'react';
import { Modal } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import type { TFunction } from 'i18next';
import { getErrorKeys, showError } from '../../../../utils/exceptionUtils';
import { DriverActivityAdminLog, TRANSLATION_STALE_MESSAGE_KEY } from '../graphql/types';
import { formatLaDateTime } from './activityFormUtils';

/**
 * 带「中文已改、英/西未同步」确认的提交：
 * 第一次 ignoreTranslationWarning=false；后端返回 translationStale 时弹确认，确认后带 true 重提。
 * 其它错误按 messageKey 走 exceptionUtils 展示。返回是否最终成功。
 */
export function submitWithTranslationStaleConfirm(
    t: TFunction,
    exec: (ignoreTranslationWarning: boolean) => Promise<unknown>
): Promise<boolean> {
    return exec(false)
        .then(() => true)
        .catch((error) => {
            if (getErrorKeys(error).messageKey !== TRANSLATION_STALE_MESSAGE_KEY) {
                showError(error);
                return false;
            }
            return new Promise<boolean>((resolve) => {
                Modal.confirm({
                    width: 460,
                    icon: <ExclamationCircleFilled style={{ color: '#FAAD14' }} />,
                    title: t('driverOps.activity.translationStale.title'),
                    content: t('driverOps.activity.translationStale.content'),
                    okText: t('driverOps.activity.translationStale.ok'),
                    cancelText: t('common.cancel'),
                    onOk: () =>
                        exec(true)
                            .then(() => resolve(true))
                            .catch((retryError) => {
                                showError(retryError);
                                resolve(false);
                            }),
                    onCancel: () => resolve(false),
                });
            });
        });
}

/** 操作日志一行：「MM-DD HH:mm · 人物 · 动作」，系统动作人物显示 i18n「系统」 */
export function formatActivityLog(log: DriverActivityAdminLog, t: TFunction, pattern: 'short' | 'full' = 'short'): string {
    const operator = log.operatorName?.trim() ? log.operatorName : t('driverOps.activity.system');
    const action = t(`driverOps.activity.log.${log.operation}`, { defaultValue: log.operation });
    return `${formatLaDateTime(log.createdAt, pattern)} · ${operator} · ${action}`;
}
